<i18n>
{
  "en": {
    "optimizerTitle": "Optimizer",
    "priceProfitabilityError": "Error loading prices profitability. Please, try it later",
    "outOfMenuError": "Error loading out of menu products. Please, try again later",
    "generatingOutOfMenu": "Generating products out of menu",
    "priceChangeError": "Error loading list of price changes. Please, try again later.",
    "priceChangeActiveWaitMessage": "Generating list of price change of products.",
    "outOfMenuTitle": "Out of Menu",
    "periodNotification": "Attention, you have pending past periods, in the period {startDate} to {endDate}"
  },
  "es": {
    "optimizerTitle": "Optimizador",
    "priceProfitabilityError": "Error cargando previsión de rentabilidad en precios. Inténtelo de nuevo más tarde.",
    "outOfMenuError": "Error cargando productos fuera de menú. Inténtelo de nuevo más tarde",
    "priceChangeError": "Error cargando la lista de cambio de precios. Inténtelo de nuevo más tarde",
    "generatingOutOfMenu": "Generando productos fuera de menú",
    "priceChangeActiveWaitMessage": "Generando el archivo de cambio de precios de productos.",
    "outOfMenuTitle": "Fuera de Carta",
    "periodNotification":  "Atención, tiene periodos pasados pendientes, en el periodo {startDate} a {endDate}"
  }
}
</i18n>

<template>
  <v-container fluid grid-list-sm>
    <div class="d-flex justify-space-around mb-3">
      <period-picker :periods="predictionPeriodList" :selected-period="selectedPeriod"
                    @period-selected="onSelectPeriod"/>
    </div>
    <optimizer-panel @optimize="onOptimize" @forecast="onForecast" @engineer="onEngineer"
                     @preview="onPreview" @out-of-menu="onOutOfMenu" @price-change="onPriceChange"
                     :current-period="selectedPeriod"/>
    <menu-preview-modal :visible="previewModalVisible" @cancel="onMenuPreviewCancel" @confirm="onMenuPreviewConfirm"/>
    <dynameat-confirmation-modal :visible="isActiveWaiterOn" :title="titleOfActiveWaiter"
                                 description="" confirm-text="" :is-loading="isActiveWaiterOn"/>
  </v-container>
</template>

<script>

import DynameatDropdown from '@dynameat/components/DynameatDropdown';
import Component from 'vue-class-component';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import PeriodPicker from '@optimizer/components/PeriodPicker';
import { createErrorNotification, createWarningNotification } from '@ulibs/unnotificationsqueue';
import { Watch } from 'vue-property-decorator';
import MenuPreviewModal from '@menuPreview/components/MenuPreviewModal.vue';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import * as Sentry from '@sentry/vue';
import OptimizerPanel from '../components/OptimizerPanel.vue';

@Component
@Components({
  PeriodPicker, OptimizerPanel, DynameatDropdown, MenuPreviewModal, DynameatConfirmationModal
})
@MapActions(['addNotification', 'priceProfitabilityRequest', 'outOfMenuRequest',
  'exportPriceChangesRequest', 'resetMenuEngineeringProfitability'])
@MapGetters(['predictionPeriodList', 'priceProfitability', 'selectedPointOfSale'])
export default class OptimizerView extends Vue {
  previewModalVisible = false;

  outOfMenuLoading = false;

  priceChangeLoading = false;

  created() {
    if (!this.priceProfitability.valid) this.loadPriceProfitability();
    this.showPeriodNotificationsIfNeeded();
  }

  get selectedPeriod() {
    return this.predictionPeriodList.find((period) => period.guid === this.$route.params.periodGuid);
  }

  get isActiveWaiterOn() {
    return this.outOfMenuLoading || this.priceChangeLoading;
  }

  get titleOfActiveWaiter() {
    return (this.outOfMenuLoading) ? this.$t('generatingOutOfMenu') : this.$t('priceChangeActiveWaitMessage');
  }

  async loadPriceProfitability() {
    try {
      const { periodGuid } = this.$route.params;
      if (periodGuid) await this.priceProfitabilityRequest({ periodGuid });
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('priceProfitabilityError'), null));
    }
  }

  clearMenuEngineering() {
    this.resetMenuEngineeringProfitability();
  }

  onForecast() {
    this.$router.push({ name: 'group-forecaster' });
  }

  onEngineer() {
    this.$router.push({ name: 'menu-recommendation-list' });
  }

  onOptimize() {
    this.$router.push({ name: 'productRecommendation' });
  }

  onPreview() {
    this.previewModalVisible = true;
  }

  onOutOfMenu() {
    const windowRef = window.open();
    this.onOutOfMenuAsync(windowRef);
  }

  async onOutOfMenuAsync(windowRef) {
    try {
      this.outOfMenuLoading = true;
      const outMenuTitle = this.$t('outOfMenuTitle');
      const outOfMenu = await this.outOfMenuRequest({
        posGuid: this.selectedPointOfSale.guid,
        startDate: this.selectedPeriod.startDate.toISOString().split('T')[0],
        endDate: this.selectedPeriod.endDate.toISOString().split('T')[0],
        outMenuTitle
      });
      this.outOfMenuLoading = false;
      outOfMenu.printWindow(windowRef);
    } catch (err) {
      windowRef?.close();
      Sentry.captureException(err);
      this.addNotification(createErrorNotification(this.$t('outOfMenuError'), null));
    } finally {
      this.outOfMenuLoading = false;
    }
  }

  async onPriceChange() {
    try {
      this.priceChangeLoading = true;

      const payload = {
        posGuid: this.selectedPointOfSale.guid,
        startDate: this.selectedPeriod.startDate.toISOString().split('T')[0],
        endDate: this.selectedPeriod.endDate.toISOString().split('T')[0]
      };
      await this.exportPriceChangesRequest(payload);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('priceChangeError'), null));
    } finally {
      this.priceChangeLoading = false;
    }
  }

  async showPeriodNotificationsIfNeeded() {
    this.predictionPeriodList.filter((p) => !p.isPreviousPeriod).flatMap((period) => this.showSinglePeriodNotification(period));
  }

  async showSinglePeriodNotification(period) {
    const periodLabelConfig = {
      startDate: period.startDateLocaleStr,
      endDate: period.endDateLocaleStr
    };
    if (!period.areProductPriceRecommendationsValidated && !period.areMenuEngineeringRecommendationsValidated) {
      this.addNotification(createWarningNotification(this.$t('periodNotification', periodLabelConfig), null));
    }
  }

  onSelectPeriod(newSelectedPeriod) {
    if (this.selectedPeriod && this.selectedPeriod.guid !== newSelectedPeriod.guid) {
      this.$router.replace({
        name: this.$route.name,
        params: { ...this.$route.params, periodGuid: newSelectedPeriod.guid }
      });
    }
  }

  onMenuPreviewCancel() {
    this.previewModalVisible = false;
  }

  onMenuPreviewConfirm() {
    this.previewModalVisible = false;
  }

  @Watch('$route')
  onPeriodGuidChanged(newRoute, oldRoute) {
    window.scrollTo(0, 0);
    if (newRoute.params.periodGuid !== oldRoute.params.periodGuid) {
      this.clearMenuEngineering();
      this.loadPriceProfitability();
    }
  }

  @Watch('predictionPeriodList', { immediate: true })
  onPeriodList(newVal, oldVal) {
    if (oldVal !== undefined) {
      this.showPeriodNotificationsIfNeeded();
    }
  }
}
</script>
